import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import WhatsAppButton from "../components/WhatsAppButton";

function StatusPill({ status }) {
  const map = {
    confirmed: { label: "CONFIRMED", color: "text-[#34C759] border-[#34C759]", Icon: CheckCircle2 },
    disputed: { label: "DISPUTED", color: "text-[#E11D48] border-[#E11D48]", Icon: AlertTriangle },
    pending: { label: "PENDING", color: "text-[#7C3AED] border-[#7C3AED]", Icon: Clock },
  };
  const cfg = map[status] || map.pending;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-2 border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.3em] ${cfg.color}`} data-testid="match-status-pill">
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function Screenshot({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let created = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        created = URL.createObjectURL(resp.data);
        if (alive) setUrl(created);
      } catch (e) { /* noop */ }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [path]);
  if (!url) return <div className="font-mono text-[10px] text-[#71717A]">[ LOADING SCREENSHOT... ]</div>;
  return <img src={url} alt="Match screenshot" className="w-full border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid="screenshot-img" />;
}

export default function MatchDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const refresh = async () => {
    try {
      const { data } = await api.get(`/matches/${id}`);
      setMatch(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <TactLoader />;
  if (!match) return <Panel className="font-mono text-[#71717A] text-sm">[ MATCH NOT FOUND ]</Panel>;

  const isHome = user && user !== false && user.id === match.home_id;
  const isAway = user && user !== false && user.id === match.away_id;
  const mySubmission = isHome ? match.home_submission : isAway ? match.away_submission : null;
  const canSubmit = (isHome || isAway) && !mySubmission && match.status !== "confirmed" && !match.admin_override;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Upload a screenshot first."); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("home_score", String(homeScore));
      form.append("away_score", String(awayScore));
      form.append("screenshot", file);
      await api.post(`/matches/${id}/submit`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Result submitted.");
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="match-detail-page" className="space-y-8">
      <div>
        <Link to="/fixtures" className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#7C3AED]">← Back to fixtures</Link>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">// round {match.round}</div>
            <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-wider">Match Detail</h1>
          </div>
          <StatusPill status={match.status} />
        </div>
      </div>

      {/* Scoreboard */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-8 text-center" data-testid="scoreboard">
        <div className="grid grid-cols-3 items-center gap-4">
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">HOME</div>
            <div className="font-heading text-3xl uppercase">{match.home?.username}</div>
            <div className="font-mono text-[10px] text-[#71717A]">{match.home?.game_id}</div>
            {match.home?.whatsapp && (
              <div className="mt-3 flex justify-end">
                <WhatsAppButton number={match.home.whatsapp} testId="whatsapp-home" size="sm" />
              </div>
            )}
          </div>
          <div className="font-mono text-5xl sm:text-6xl text-[#09090B] dark:text-[#F4F4F5]">
            {match.status === "confirmed" ? `${match.confirmed_score_home} : ${match.confirmed_score_away}` : "—  :  —"}
          </div>
          <div className="text-left">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">AWAY</div>
            <div className="font-heading text-3xl uppercase">{match.away?.username}</div>
            <div className="font-mono text-[10px] text-[#71717A]">{match.away?.game_id}</div>
            {match.away?.whatsapp && (
              <div className="mt-3 flex justify-start">
                <WhatsAppButton number={match.away.whatsapp} testId="whatsapp-away" size="sm" />
              </div>
            )}
          </div>
        </div>
        {match.admin_override && <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">[ RESULT FINALIZED BY ADMIN ]</div>}
      </div>

      {/* Submissions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SubmissionCard label="Home Submission" submission={match.home_submission} player={match.home} user={user} />
        <SubmissionCard label="Away Submission" submission={match.away_submission} player={match.away} user={user} />
      </div>

      {/* Submit form */}
      {user === false && (
        <Panel className="font-mono text-[#71717A] text-sm">[ SIGN IN AS ONE OF THESE PLAYERS TO SUBMIT A RESULT. ]</Panel>
      )}
      {user && user !== false && !isHome && !isAway && (
        <Panel className="font-mono text-[#71717A] text-sm">[ YOU ARE NOT PART OF THIS FIXTURE. ]</Panel>
      )}
      {canSubmit && (
        <form onSubmit={onSubmit} className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-8 space-y-6" data-testid="submit-form">
          <SectionTitle kicker="// upload tactical dropzone" className="mb-0">Submit Your Result</SectionTitle>

          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">Home Score</label>
              <input
                data-testid="input-home-score" type="number" min={0} max={99} required
                value={homeScore} onChange={(e) => setHomeScore(Number(e.target.value))}
                className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] font-mono text-xl focus:outline-none focus:ring-1 focus:ring-[#7C3AED]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">Away Score</label>
              <input
                data-testid="input-away-score" type="number" min={0} max={99} required
                value={awayScore} onChange={(e) => setAwayScore(Number(e.target.value))}
                className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] font-mono text-xl focus:outline-none focus:ring-1 focus:ring-[#7C3AED]"
              />
            </div>
          </div>

          <label className="block border-2 border-dashed border-[#E4E4E7] dark:border-[#2A2A30] p-8 text-center cursor-pointer hover:border-[#7C3AED] transition-colors" data-testid="dropzone">
            <input ref={fileRef} data-testid="input-screenshot" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" required />
            <Upload className="w-8 h-8 mx-auto text-[#7C3AED] mb-3" />
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A]">
              {file ? `> ${file.name}` : "[ CLICK TO UPLOAD FINAL-SCORE SCREENSHOT ]"}
            </div>
          </label>

          <button
            data-testid="submit-result-btn"
            type="submit" disabled={submitting}
            className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-8 py-3 hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
          >
            {submitting ? "TRANSMITTING..." : "Submit Result"}
          </button>
          <div className="font-mono text-[10px] text-[#71717A] uppercase tracking-[0.25em]">
            // Result confirms instantly if your opponent's scores match yours. Mismatches flag as disputed for admin review.
          </div>
        </form>
      )}
      {(isHome || isAway) && mySubmission && match.status !== "confirmed" && (
        <Panel className="font-mono text-xs text-[#71717A]">[ YOU'VE SUBMITTED. WAITING FOR OPPONENT. ]</Panel>
      )}
    </div>
  );
}

function SubmissionCard({ label, submission, player, user }) {
  return (
    <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5" data-testid={`submission-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{label}</div>
        {submission
          ? <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#34C759]">RECEIVED</span>
          : <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">AWAITING</span>}
      </div>
      <div className="font-heading text-lg uppercase">{player?.username || "—"}</div>
      {submission ? (
        <>
          <div className="font-mono text-2xl text-[#09090B] dark:text-[#F4F4F5] mt-2">
            {submission.score_home} : {submission.score_away}
          </div>
          {user && user !== false ? (
            <div className="mt-4">
              <Screenshot path={submission.screenshot_path} />
            </div>
          ) : (
            <div className="mt-4 font-mono text-[10px] text-[#71717A]">[ SIGN IN TO VIEW SCREENSHOT ]</div>
          )}
        </>
      ) : (
        <div className="font-mono text-[#71717A] text-sm mt-2">[ NOT YET SUBMITTED ]</div>
      )}
    </div>
  );
}
